<template>
  <InfuseSpinner class="flex items-center justify-center p-16" v-if="isCentralSystemsLoading" size="medium" />

  <div v-else>
    <InfuseNote class="mb-8" v-if="!isCentralSystemsLoading && linkedCentralSystems.length === 0 && !searchQuery">
      {{ t('e_connector.chargingStationLinks.noLinkedCentralSystems') }}
    </InfuseNote>

    <LinkedCentralSystemTable
      :create-enabled="true"
      :editable="true"
      :loading="isCentralSystemsLoading"
      :removable="true"
      :rows="filteredLinkedCentralSystems"
      :search-enabled="searchEnabled"
      :search-query="searchQuery"
      @create="createModalVisible = true"
      @edit="editRow"
      @remove="(linkedCentralSystem: LinkedCentralSystem) => confirmRemove(linkedCentralSystem)"
      @search="(query: string) => search(query)"
    />

    <InfuseNote class="mt-4" v-if="inaccessibleCount > 0">
      {{ t('e_connector.chargingStationLinks.inaccessibleCentralSystems', { count: inaccessibleCount }) }}
    </InfuseNote>
  </div>

  <CreateLinkedCentralSystemModal
    v-if="createModalVisible"
    :loading="isUpserting"
    :central-systems="centralSystems"
    :charging-station="model"
    @close="createModalVisible = false"
    @create="(linkedCentralSystem: LinkedCentralSystem, newCentralSystem?: NewCentralSystemDefinition) => create(linkedCentralSystem, newCentralSystem)"
  />

  <UpdateLinkedCentralSystemModal
    v-if="updateModalVisible && selectedRow"
    :loading="isUpserting"
    :linked-central-system="selectedRow"
    :central-systems="centralSystems"
    :charging-station="model"
    @close="updateModalVisible = false"
    @update="(linkedCentralSystem: LinkedCentralSystem) => update(linkedCentralSystem)"
  />

  <InfuseConfirmModal
    v-if="removeModalVisible"
    :action-title="t('e_connector.chargingStationLinks.modal.remove.title')"
    verification-text="delete"
    @confirm="remove"
    @close="removeModalVisible = false"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useQueryClient } from '@tanstack/vue-query';
import { InfuseConfirmModal, InfuseNote, InfuseSpinner } from '@infuse/ui/components';
import {
  CreateLinkedCentralSystemModal,
  LinkedCentralSystemTable,
  NewCentralSystemDefinition,
  UpdateLinkedCentralSystemModal
} from '@infuse/e-connector/charging-station/ui';
import { replaceQueryParams } from '@infuse/ui/utils';
import { ConnectionType, LinkedCentralSystem, mapToDomain as mapCentralSystemToDomain } from '@infuse/e-connector/central-system/domain';
import { ChargingStation } from '@infuse/e-connector/charging-station/domain';
import { hydrateLinkedCentralSystems, LinkedCentralSystemHydrated } from '@infuse/e-connector/shared/feature';
import { createEconnectorClient } from '@infuse/api/common';
import { CentralSystem as ApiCentralSystem } from '@infuse/api/e-connector/central-system';
import { upsertCentralSystemLinkDirect } from './util';

const { t } = useI18n();

const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();

const allCentralSystems = ref<ApiCentralSystem[]>([]);
const isCentralSystemsLoading = ref(false);
const isUpserting = ref(false);
const centralSystems = computed(() => allCentralSystems.value.map(mapCentralSystemToDomain));

const client = createEconnectorClient('/api');

const fetchCentralSystems = async () => {
  isCentralSystemsLoading.value = true;
  try {
    // Loop all pages: the backend paginates (default size 20), so a single
    // request would silently drop any central system beyond the first page.
    // Links to those would show as "inaccessible" and adding them would fail.
    const all: ApiCentralSystem[] = [];
    let page = 0;
    let last = false;
    while (!last) {
      const response = await client.get<{ content?: ApiCentralSystem[]; last?: boolean } | ApiCentralSystem[]>(
        'centralsystems',
        { params: { page, size: 20 } }
      );
      if (Array.isArray(response.data)) {
        all.push(...response.data);
        last = true;
      } else {
        all.push(...(response.data?.content ?? []));
        last = response.data?.last ?? true;
      }
      page++;
    }
    allCentralSystems.value = all;
  } catch (e) {
    console.error('fetchCentralSystems failed:', e);
  } finally {
    isCentralSystemsLoading.value = false;
  }
};

const searchQuery = ref<string | undefined>(route.query.search?.toString());

const createModalVisible = ref<boolean>(false);
const updateModalVisible = ref<boolean>(false);
const removeModalVisible = ref<boolean>(false);
const selectedRow = ref<LinkedCentralSystem>();

const linkedCentralSystems = ref<LinkedCentralSystemHydrated[]>([]);
const filteredLinkedCentralSystems = ref<LinkedCentralSystemHydrated[]>([]);
const inaccessibleCount = ref<number>(0);

interface Emits {
  (e: 'reload-model'): void;
}
const emit = defineEmits<Emits>();

interface Props {
  model: ChargingStation;
  searchEnabled?: boolean;
  routeName?: string;
}
const props = withDefaults(defineProps<Props>(), {
  searchEnabled: true,
  routeName: 'charging-stations.linkedSystems'
});

onMounted(async () => {
  await fetchCentralSystems();
  rehydrateLinkedCentralSystems();
});

watch(() => props.model, rehydrateLinkedCentralSystems, { deep: true });
watch(centralSystems, rehydrateLinkedCentralSystems);

async function afterMutation() {
  // Invalidate vue-query cache so the e-connector parent refetches the station
  await queryClient.invalidateQueries({ queryKey: ['charging-stations'] });
  // Also emit for parents that don't use vue-query (e.g. ChargingStationFromRouteLinksTab)
  emit('reload-model');
}

async function search(search: string) {
  searchQuery.value = search;
  replaceQueryParams(router, props.routeName, { search: searchQuery.value });

  if (search) {
    filteredLinkedCentralSystems.value = linkedCentralSystems.value.filter((linkedCentralSystem) =>
      [
        ...Object.values(linkedCentralSystem),
        ...Object.values(linkedCentralSystem.chargingStation),
        ...Object.values(linkedCentralSystem.centralSystem)
      ].some((v) => v?.toString().toLowerCase().includes(search.toLowerCase()))
    );
  } else {
    filteredLinkedCentralSystems.value = [...linkedCentralSystems.value];
  }
}

async function create(linkedCentralSystem: LinkedCentralSystem, newCentralSystem?: NewCentralSystemDefinition) {
  if (newCentralSystem) {
    isUpserting.value = true;
    try {
      const response = await client.post<ApiCentralSystem>('centralsystems', {
        name: newCentralSystem.name,
        uri: newCentralSystem.uri,
        type: ConnectionType.WEBSOCKET,
        ocppVersion: newCentralSystem.ocppVersion
      });
      const created = response.data;
      allCentralSystems.value = [...allCentralSystems.value, created];
      linkedCentralSystem.centralSystemId = created.id;
    } catch (e) {
      console.error('createCentralSystem failed:', e);
      isUpserting.value = false;
      return;
    } finally {
      isUpserting.value = false;
    }
  }
  await upsertCentralSystemLinkDirect(linkedCentralSystem, isUpserting, allCentralSystems, client);
  createModalVisible.value = false;
  await afterMutation();
}

async function update(linkedCentralSystem: LinkedCentralSystem) {
  await upsertCentralSystemLinkDirect(linkedCentralSystem, isUpserting, allCentralSystems, client);
  updateModalVisible.value = false;
  await afterMutation();
}

function editRow(row: LinkedCentralSystem) {
  selectedRow.value = row;
  updateModalVisible.value = true;
}

function confirmRemove(row: LinkedCentralSystem) {
  selectedRow.value = row;
  removeModalVisible.value = true;
}

async function remove() {
  if (!selectedRow.value) {
    return;
  }

  await upsertCentralSystemLinkDirect(selectedRow.value, isUpserting, allCentralSystems, client, true);

  removeModalVisible.value = false;
  selectedRow.value = undefined;
  await afterMutation();
}

function rehydrateLinkedCentralSystems() {
  if (!props.model?.linkedCentralSystems?.length) {
    return;
  }
  linkedCentralSystems.value = centralSystems.value.length
    ? (hydrateLinkedCentralSystems(props.model, centralSystems.value) ?? [])
    : [];
  inaccessibleCount.value = props.model.linkedCentralSystems.length - linkedCentralSystems.value.length;
  filteredLinkedCentralSystems.value = [...linkedCentralSystems.value];
}
</script>
